import React, { useMemo } from "react";
import Select, { components } from "react-select";
import { Building } from "lucide-react";
import customSelectStyles from "../../../components/ui/customSelectStyles";

/** Loại chi nhánh (`hrm.branches.affiliation_type`) */
const AFFILIATION_LABEL = {
  hq: "Tổng công ty",
  affiliated: "Trực thuộc",
  franchise: "Nhượng quyền",
};

export function affiliationLabelForBranch(branch) {
  const raw = String(branch?.affiliation_type || "")
    .trim()
    .toLowerCase();
  if (raw === "hq") return AFFILIATION_LABEL.hq;
  if (raw === "franchise") return AFFILIATION_LABEL.franchise;
  return AFFILIATION_LABEL.affiliated;
}

/** Hiển thị tên đại diện dạng «tên • họ» khi lưu kiểu Việt «Họ Tên» (2 từ). */
export function formatRepresentativeDisplay(managerName) {
  const s = String(managerName || "").trim();
  if (!s) return "";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 2) return `${parts[1]} ${parts[0]}`;
  return s;
}

export function branchSubtitle(branch) {
  const type = affiliationLabelForBranch(branch);
  const rep = formatRepresentativeDisplay(branch?.manager);
  if (rep) return `${type} • ${rep}`;
  return type;
}

/** Mục "Tất cả" — giống /hrm/dashboard */
export function buildHrmDashboardBranchSelectOptions(branchOptions = []) {
  const allOpt = {
    value: "all",
    label: "Tất cả chi nhánh",
    sub: "Xem gộp mọi chi nhánh",
  };
  if (!branchOptions.length) return [allOpt];
  return [
    allOpt,
    ...branchOptions.map((b) => ({
      value: String(b.id),
      label: String(b.name || "—"),
      sub: branchSubtitle(b),
    })),
  ];
}

const BranchMenuOption = (props) => {
  const { data, isSelected } = props;
  return (
    <components.Option {...props}>
      <div className="py-0.5 text-left">
        <div
          className={`text-sm font-black uppercase tracking-tight leading-snug ${
            isSelected ? "text-white" : "text-gray-800"
          }`}
        >
          {data.label}
        </div>
        {data.sub ? (
          <div
            className={`mt-1 text-[10px] font-bold leading-snug normal-case tracking-normal ${
              isSelected ? "text-red-100" : "text-gray-400"
            }`}
          >
            {data.sub}
          </div>
        ) : null}
      </div>
    </components.Option>
  );
};

const BranchSingleValue = (props) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div className="flex min-w-0 flex-col py-0.5 text-left">
        <span className="truncate text-sm font-black uppercase tracking-tight text-gray-800">{data.label}</span>
        {data.sub ? (
          <span className="truncate text-[10px] font-bold normal-case tracking-normal text-gray-400">{data.sub}</span>
        ) : null}
      </div>
    </components.SingleValue>
  );
};

export const branchDashboardSelectStyles = {
  ...customSelectStyles,
  control: (provided, state) => ({
    ...(typeof customSelectStyles.control === "function"
      ? customSelectStyles.control(provided, state)
      : provided),
    minHeight: 50,
    height: "auto",
    borderRadius: "1rem",
    borderColor: state.isFocused ? "#fecaca" : "#f3f4f6",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(254, 202, 202, 0.45)" : "none",
  }),
  valueContainer: (provided) => ({
    ...provided,
    paddingLeft: 40,
    paddingTop: 6,
    paddingBottom: 6,
  }),
  singleValue: (provided, state) => ({
    ...(typeof customSelectStyles.singleValue === "function"
      ? customSelectStyles.singleValue(provided, state)
      : provided),
    position: "relative",
    transform: "none",
    top: "auto",
    maxWidth: "100%",
    margin: 0,
  }),
  option: (provided, state) => ({
    ...(typeof customSelectStyles.option === "function" ? customSelectStyles.option(provided, state) : provided),
    paddingTop: 10,
    paddingBottom: 10,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
};

/**
 * Dropdown chi nhánh dùng trên `/hrm/dashboard` — tái sử dụng tại chỗ khác trong HRM.
 * `value` / `onChange`: chuỗi `"all"` hoặc `id` chi nhánh (theo backend).
 */
export default function HrmDashboardBranchPicker({
  instanceId,
  className = "",
  classNamePrefix = "hrm-dash-branch",
  options,
  value,
  onChange,
  /** Đồng bộ chiều cao với input h-11 (44px) — dùng trên form HCNS xác nhận công */
  compactControlHeight = false,
  /** Chiều cao cố định control (px). Ưu tiên hơn compactControlHeight khi có giá trị. */
  controlHeight = null,
  "aria-label": ariaLabel,
}) {
  const selectValue = useMemo(
    () => options.find((o) => String(o.value) === String(value)) ?? options[0],
    [options, value]
  );

  const mergedStyles = useMemo(() => {
    const fixedHeight =
      controlHeight != null && Number(controlHeight) > 0
        ? Number(controlHeight)
        : compactControlHeight
          ? 44
          : null;

    if (!fixedHeight) return branchDashboardSelectStyles;

    return {
      ...branchDashboardSelectStyles,
      control: (provided, state) => {
        const base =
          typeof branchDashboardSelectStyles.control === "function"
            ? branchDashboardSelectStyles.control(provided, state)
            : provided;
        return { ...base, minHeight: fixedHeight, height: fixedHeight };
      },
      valueContainer: (provided) => {
        const base = branchDashboardSelectStyles.valueContainer(provided);
        return { ...base, paddingTop: 4, paddingBottom: 4 };
      },
      indicatorsContainer: (provided) => ({
        ...provided,
        height: fixedHeight,
      }),
    };
  }, [compactControlHeight, controlHeight]);

  return (
    <div className={`relative z-10 min-w-0 ${className}`.trim()}>
      <div className="pointer-events-none absolute inset-y-0 left-4 z-[1] flex items-center text-red-600">
        <Building size={16} strokeWidth={3} />
      </div>
      <Select
        instanceId={instanceId}
        aria-label={ariaLabel}
        options={options}
        value={selectValue}
        onChange={(opt) => onChange(opt?.value ?? "all")}
        styles={mergedStyles}
        components={{ Option: BranchMenuOption, SingleValue: BranchSingleValue }}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        menuPosition="fixed"
        isSearchable={false}
        classNamePrefix={classNamePrefix}
      />
    </div>
  );
}
